/**
 * Core channel names used for the live map and initial history load.
 * Processor-mapped names (same as in LIVE_CHANNELS); single source of truth for
 * "required" live stream channels when client-side filtering or a history merge is needed.
 */
export const LIVE_STREAM_CHANNELS: string[] = [
  'Lat_dd',
  'Lng_dd',
  'Bsp_kph',
  'Hdg_deg',
  'Tws_kph',
  'Twd_deg',
  'Twa_deg',
  'GRADE',
];

/**
 * Canonical list of channel names available from the streaming server.
 * Matches the normalized channel names produced by the streaming processor
 * (server_stream/controllers/processor.js) and documented in the streaming API notes.
 * Used by the Live Table channel picker so users can select from all known live channels
 * even before data has arrived.
 */
export const LIVE_CHANNELS: string[] = [
  // Basic navigation
  'Lat_dd',
  'Lng_dd',
  'Hdg_deg',
  'Cog_deg',
  // Speed
  'Bsp_kph',
  'Bsp_kts',
  'Sog_kph',
  'Sog_kts',
  'Vmg_kph',
  'Vmg_kts',
  // Wind - canonical (aliases the team-model solution; see WIND_CANONICAL_ALIAS in processor.js)
  'Twd_deg',
  'Tws_kph',
  'Tws_kts',
  'Twa_deg',
  'Twa_n_deg',
  'Awa_deg',
  'Awa_n_deg',
  'Aws_kph',
  'Aws_kts',
  'Cwa_deg',
  'Cwa_n_deg',
  // Wind - per-sensor (masthead / bow). Canonical = the TM solution, matching the
  // fused channels above and the batch pipeline; the SGP vanes are listed further down.
  'Twd_bow_deg',
  'Twd_mhu_deg',
  'Twa_bow_deg',
  'Twa_mhu_deg',
  'Twa_bow_n_deg',
  'Twa_mhu_n_deg',
  'Awa_bow_deg',
  'Awa_mhu_deg',
  'Awa_bow_n_deg',
  'Awa_mhu_n_deg',
  'Tws_bow_kph',
  'Tws_mhu_kph',
  'Aws_bow_kph',
  'Aws_mhu_kph',
  // Wind - SGP solution (kept separate from the canonical channels for A/B)
  'Twd_sgp_deg',
  'Twa_sgp_deg',
  'Awa_sgp_deg',
  'Tws_sgp_kph',
  'Aws_sgp_kph',
  'Twd_bow_sgp_deg',
  'Twd_mhu_sgp_deg',
  'Twa_bow_sgp_deg',
  'Twa_mhu_sgp_deg',
  'Awa_bow_sgp_deg',
  'Awa_mhu_sgp_deg',
  'Tws_bow_sgp_kph',
  'Tws_mhu_sgp_kph',
  'Aws_bow_sgp_kph',
  'Aws_mhu_sgp_kph',
  // Wind - team-model (TM) solution, less dampened than SGP
  'Twd_tm_deg',
  'Twa_tm_deg',
  'Twa_tm_n_deg',
  'Awa_tm_deg',
  'Awa_tm_n_deg',
  'Tws_tm_kph',
  'Aws_tm_kph',
  'Twd_bow_tm_deg',
  'Twd_mhu_tm_deg',
  'Twa_bow_tm_deg',
  'Twa_mhu_tm_deg',
  'Awa_bow_tm_deg',
  'Awa_mhu_tm_deg',
  'Tws_bow_tm_kph',
  'Tws_mhu_tm_kph',
  'Aws_bow_tm_kph',
  'Aws_mhu_tm_kph',
  // Wind shear (masthead minus bow, tack-normalized)
  'Twa_shear_deg',
  // Fleet wind
  'Tws_fleet_kts',
  'Twd_fleet_deg',
  // Performance
  'Bsp_perc',
  'Vmg_perc',
  'Polar_perc',
  // Targets (processor.js maps targ_twa_deg / targ_boat_speed / targ_vmg from the team model)
  'Twa_tgt_deg',
  'Bsp_tgt_kph',
  'Vmg_tgt_kph',
  // Leeway
  'Lwy_deg',
  'Lwy_n_deg',
  'Lwy2_deg',
  'Lwy2_n_deg',
  // Heel, pitch and rates
  'Heel_deg',
  'Heel_n_deg',
  'Pitch_deg',
  'Pitch_rate_dps',
  'Roll_rate_dps',
  'Roll_rate_n_dps',
  'Yaw_rate_dps',
  'Yaw_rate_n_dps',
  'Accel_rate_mps2',
  // Foiling state
  'Foiling_state',
  // CA channels
  'CA1_ang_n_deg',
  'CA2_ang_n_deg',
  'CA3_ang_n_deg',
  'CA4_ang_n_deg',
  'CA5_ang_n_deg',
  'CA6_ang_n_deg',
  // WING channels
  'WING_twist_n_deg',
  'WING_rot_deg',
  'WING_rot_n_deg',
  'WING_aoa_n_deg',
  'WING_clew_ang_n_deg',
  'WING_clew_pos_n_mm',
  // Rudder
  'RUD_ang_n_deg',
  'RUD_imm_lwd_mm',
  'RUD_imm_wwd_mm',
  'RUD_imm_tot_mm',
  // Daggerboard - LWD/WWD
  'DB_rake_ang_lwd_deg',
  'DB_rake_ang_wwd_deg',
  'DB_rake_aoa_lwd_deg',
  'DB_rake_aoa_wwd_deg',
  'DB_ext_lwd_mm',
  'DB_ext_wwd_mm',
  'DB_cant_lwd_deg',
  'DB_cant_wwd_deg',
  'DB_cant_eff_lwd_deg',
  'DB_cant_eff_wwd_deg',
  'DB_imm_lwd_mm',
  'DB_imm_wwd_mm',
  'DB_piercing_lwd_mm',
  'DB_piercing_wwd_mm',
  // Rigging
  'RH_lwd_mm',
  'RH_wwd_mm',
  'LOAD_CA1_tf',
  'SHRD_lwr_port_tf',
  'SHRD_lwr_stbd_tf',
  'SHRD_upr_port_tf',
  'SHRD_upr_stbd_tf',
  'SHRD_lwr_lwd_tf',
  'SHRD_lwr_wwd_tf',
  'SHRD_upr_lwd_tf',
  'SHRD_upr_wwd_tf',
  'BOBSTAY_load_tf',
  'RIG_load_tf',
  // JIB
  'JIB_sheet_load_kgf',
  'JIB_lead_ang_deg',
  'JIB_cunno_load_kgf',
  'JIB_lead_pct',
  'JIB_sheet_pct',
  // State (computed)
  'TACK',
  'POINTOFSAIL',
  'MANEUVER_TYPE',
  'GRADE',
  // Metadata / identifiers
  'Race_number',
  'Leg_number',
];
